import { axiosInstance } from "api/axios";
import Loading from "components/Loading";
import KeysContext from "contexts/KeysContext";
import {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useInfiniteQuery } from "react-query";
import { useParams } from "react-router-dom";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import { EOrder } from "components/SortOrder/typings";
import usePDF from "hooks/usePDF";
import ChatMessagesContext from "contexts/ChatMessagesContext";
import chatService from "api/chatService";
import ChatMessage from "components/ChatMessage";
import { SignalRContext } from "../../App";
import ChatViewNewMessage from "components/ChatViewNewMessage";
import { IChatMessageAttachment } from "dto/Chat/IChatMessageAttachment";
import { IChatMessage } from "dto/Chat/IChatMessage";
import { ItemKind } from "dto/IKindItems";
import { DossierItemType } from "dto/Dossier/DossierItemType";
import PDFViewer from "components/PDFViewer";
import IssuesContext from "contexts/IssuesContext";
import ChatOptions from "components/ChatOptions";
import PersonsIcon from "assets/icons/groups.svg?react";
import ChatParticipantsModal from "components/ChatParticipantsModal";
import { IChatParticipant } from "dto/Chat/IChat";
import InputInline from "components/InputInline";
import ChatsContext from "contexts/ChatsContext";
import moment from "moment";
import { HubCallbacksNames } from "api/hub";

const ChatView: FC<Props> = ({ chatId }) => {
  const { view } = useParams();

  const [order] = useState(EOrder.DESCENDING);


  const { data: chatData, refetch: refetchChat } =
    chatService.useGetChat(chatId);
  const { mutate: updateChat } = chatService.useUpdateChat();

  const [showComments, setShowComments] = useState(false);
  const [previewFile, setPreviewFile] = useState<IChatMessageAttachment>(null);
  const [editName, setEditName] = useState(false);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [participants, setParticipants] = useState<IChatParticipant[]>([]);

  const containerRef = useRef(null);
  const observerParentRef = useRef(null);
  const {
    data: packageData,
    fetchNextPage,
    isLoading: isL,
    isFetchingNextPage,
    refetch,
    status,
    hasNextPage,
  } = useInfiniteQuery(
    ["chatt", order, view, chatId],
    async ({ pageParam }) => {
      const query = new URLSearchParams(window.location.search);

      query.append("ChatId", chatId.toString());
      query.append("Page", pageParam ?? "0");

      const res = await axiosInstance.get(`/chat/messages?${query}`);
      return res.data;
    },
    {
      // enabled: false,
      staleTime: 0,
      cacheTime: 0,
      getNextPageParam: (lastPage, allPages) => {
        console.log(lastPage, allPages);
        if (!lastPage.LastPage) {
          return lastPage.Page + 1;
        }
        return undefined;
      },
    }
  );

  SignalRContext.useSignalREffect(
    "newChatMessage",
    (message: any) => {
      if (message.chatId === chatId) {
        refetchChat();
        refetch();
      }
    },
    []
  );

  const { keys, setKeys } = useContext(KeysContext);
  const { messages, setMessages } = useContext(ChatMessagesContext);
  const { issues, setIssues } = useContext(IssuesContext);
  const { chats, setChats } = useContext(ChatsContext);

  SignalRContext.useSignalREffect(
    HubCallbacksNames.Online,
    (message) => {
      const oldList = [...participants];
      const toUpdate = participants.find((p) => p.UserId === message.userId);

      if (toUpdate) {
        toUpdate.IsOnline = true;
        setParticipants(oldList);
      }
    },
    []
  );

  SignalRContext.useSignalREffect(
    HubCallbacksNames.Offline,
    (message) => {
      const oldList = [...participants];
      const toUpdate = participants.find((p) => p.UserId === message.userId);

      if (toUpdate) {
        toUpdate.IsOnline = false;
        setParticipants(oldList);
      }
    },
    []
  );


  useEffect(() => {
    setMessages([]);
    refetchChat();
  }, [chatId]);

  useEffect(() => {
    if (chatData?.data) {
      setKeys([...keys, chatData.data.EncryptionKey]);

      if (chatData?.data.Unread) {
        const newIssues = [...issues];
        const newChats = [...chats];

        const toUpdate = newIssues.find((x) => x.ChatId === chatId);
        const toUpdate2 = chats.find((x) => x.Id === chatId);
        if (toUpdate) {
          toUpdate.Unread = false;
          setIssues(newIssues);
        }

        if (toUpdate2) {
          toUpdate2.Unread = false;
          setChats(newChats);
        }
      }

      setParticipants(chatData?.data?.Participants);
      setName(chatData.data.Name);
    }
  }, [chatData, chatData?.data]);

  const [isInitialized, setIsInitialized] = useState(false);

  const compileData = async () => {
    console.log("Compile data");
    if (!packageData) return;

    const newPages = [...packageData?.pages];

    const packs = await Promise.all(
      newPages
        .sort((a, b) => b.Page - a.Page)
        .map((page: any) => {
          return page.Items;
        })
        .flat()
    );

    const historyItems = await Promise.all(
      newPages
        .sort((a, b) => b.Page - a.Page)
        .map((page: any) => {
          return page.History.map(
            (h) =>
              ({
                Id: "history" + h.Id,
                CreationDate: h.CreationDate,
                Content: h.Message,
                SenderLabel: null,
                Attachments: [],
                Incoming: false,
                IsHistoryItem: true,
              } as IChatMessage)
          );
        })
        .flat()
    );

    const all = [...packs, ...historyItems].sort(
      (a, b) =>
        new Date(a.CreationDate).getTime() - new Date(b.CreationDate).getTime()
    );

    setMessages(all);
    setIsInitialized(true);
    // const objectKeys: any[] = await Promise.all(
    //   packageData?.pages
    //     .map((page: any) => {
    //       return page.ObjectKeys;
    //     })
    //     .flat()
    // );

    // const keys: any[] = await Promise.all(
    //   packageData?.pages
    //     .map((page: any) => {
    //       return page.Keys;
    //     })
    //     .flat()
    // );

    // //@ts-ignore
    // setKeys((prev) =>
    //   [...prev, objectKeys, keys].filter((f) => f !== undefined).flat()
    // );
  };

  const [lastFetchTime, setLastFetchTime] = useState(0);
  const fetchThreshold = 1000; // 1 second

  const topRef = useRef(null);
  const previousScrollHeightRef = useRef(0);
  const shouldRestoreScrollRef = useRef(false);
  const rightHeaderRef = useRef(null);
  const [name, setName] = useState<string>(chatData?.data?.Name);
  const [showOptions, setShowOptions] = useState<boolean>(false);

  const updateParticipants = (participants: IChatParticipant[]) => {
    // todo: przegenerowanie kluczy

    updateChat(
      {
        ChatId: chatId,
        Participants: participants.map((x) => ({ UserId: x.UserId })),
      },
      {
        onSuccess(data) {
          const newChats = [...chats];
          const toUpdate = newChats.find((x) => x.Id === chatId);
          toUpdate.Participants = data.data.Participants;
          setChats(newChats);
          setParticipants(participants);

          refetch();
        },
      }
    );
  };

  useEffect(() => {
    const container = containerRef.current;
    const topDiv = topRef.current;
    if (!container || !topDiv) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!isInitialized) return;

        const currentTime = Date.now();
        if (currentTime - lastFetchTime < fetchThreshold) return;

        const firstEntry = entries[0];

        if (firstEntry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          previousScrollHeightRef.current = container.scrollHeight;
          setLastFetchTime(currentTime);
          shouldRestoreScrollRef.current = true;
          fetchNextPage();
        }
      },
      {
        root: container, // container is the scrollable area
        threshold: 1.0, // fully visible
      }
    );

    observer.observe(topDiv);

    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    compileData();
  }, [packageData]);

  // Restore scroll after new messages are rendered
  useLayoutEffect(() => {
    if (shouldRestoreScrollRef.current && containerRef.current) {
      const container = containerRef.current;
      const scrollDiff =
        container.scrollHeight - previousScrollHeightRef.current;
      container.scrollTop += scrollDiff;
      shouldRestoreScrollRef.current = false;
    }
  }, [messages.length]);

  const {
    blob,
    create,
    download,
    removeBlob,
  } = usePDF();
  const [, setDownloadFileName] = useState("");

  const onDownload = async (
    pack: IChatMessage,
    attachment: IChatMessageAttachment
  ) => {
    setDownloadFileName(attachment.FileName);
    download(
      pack.Id,
      {
        Id: attachment.Id.toString(),
        Uri: attachment.Uri,
        FileName: attachment.FileName,
        CommentsCount: 0,
        ContentType: attachment.ContentType,
        CreationDate: pack.CreationDate,
        DeskInfo_IsOn: false,
        DossierId: null,
        EncryptionKeyId: chatData?.data.EncryptionKeyId,
        Format: null,
        IsShared: false,
        Size: attachment.Size,
        Kind: ItemKind.ChatMessage,
        Keys: [],
        Label: null,
        LastModified: pack.CreationDate,
        ObjectKeys: [],
        ShowInDossier: 0,
        TagCount: 0,
        Type: DossierItemType.Attachment,
      },
      ItemKind.ChatMessage
    );
  };

  const onPreview = async (
    pack: IChatMessage,
    attachment: IChatMessageAttachment
  ) => {
    setPreviewFile(attachment);
    create(
      pack.Id,
      {
        Id: attachment.Id.toString(),
        Uri: attachment.Uri,
        FileName: attachment.FileName,
        CommentsCount: 0,
        ContentType: attachment.ContentType,
        CreationDate: pack.CreationDate,
        DeskInfo_IsOn: false,
        DossierId: null,
        EncryptionKeyId: chatData?.data.EncryptionKeyId,
        Format: null,
        IsShared: false,
        Size: attachment.Size,
        Kind: ItemKind.ContactMessage,
        Keys: [],
        Label: null,
        LastModified: pack.CreationDate,
        ObjectKeys: [],
        ShowInDossier: 0,
        TagCount: 0,
        Type: DossierItemType.Attachment,
      },
      ItemKind.ChatMessage,
      false
    );
  };

  const updateName = (name: string) => {
    updateChat(
      {
        ChatId: chatId,
        Name: name,
      },
      {
        onSuccess() {
          const newChats = [...chats];

          const toUpdate = newChats.find((x) => x.Id === chatId);
          toUpdate.Name = name;
          setChats(newChats);
          setName(name);
          setEditName(false);
          refetch();
        },
      }
    );
  };


  // useEffect(() => {
  //   if (!blob) return;
  //   console.log("Downloading!");
  //   const URL = window.URL.createObjectURL(blob);
  //   const link = document.createElement("a");
  //   link.href = URL;
  //   link.setAttribute("download", downloadFileName);
  //   document.body.appendChild(link);
  //   link.click();
  //   window.URL.revokeObjectURL(URL);
  //   removeBlob();
  // }, [blob]);

  const pdfRef = useRef<HTMLDivElement>();

  useEffect(() => {
    if (pdfRef.current) {
      console.log("adding show class");
      pdfRef.current.classList.add(styles.show);
    }
  }, [pdfRef.current]);

  // Scroll to bottom on first load
  useEffect(() => {
    if (containerRef.current && status === "success") {
      const container = containerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  }, [status, containerRef]);

  if (isL || !chatData?.data) {
    return (
      <div className={styles.loadingWrapper}>
        <Loading />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.left}>
          <div
            onClick={() => setShowParticipantsModal(!showParticipantsModal)}
            className={styles.persons}
          >
            <PersonsIcon className={styles.personIcon} />
          </div>
          <div onClick={() => {}} className={styles.title}>
            {editName && (
              <InputInline
                value={name}
                onChange={updateName}
                onCancel={() => setEditName(false)}
              ></InputInline>
            )}
            {!editName && name}
          </div>
        </div>
        <div className={styles.right} ref={rightHeaderRef}>
          {chatData?.data && (
            <ChatOptions
              chat={chatData?.data}
              showOptions={showOptions}
              refWrapper={rightHeaderRef}
              toggle={() => setShowOptions(false)}
              setEditName={setEditName}
              setShowParticipants={setShowParticipantsModal}
            ></ChatOptions>
          )}
        </div>
      </div>
      <div className={styles.chat} ref={containerRef}>
        <div className={styles.other} ref={observerParentRef}>
          {(!isInitialized || hasNextPage || isFetchingNextPage) && (
            <div ref={topRef} style={{ height: "100px" }} />
          )}

          {messages.map((m, index) =>
            !m.IsHistoryItem ? (
              <ChatMessage
                key={m.Id}
                Package={m}
                encryptionKeyId={chatData?.data?.EncryptionKeyId}
                index={index}
                keys={keys}
                onDownload={(m, a) => {
                  onDownload(m, a);
                }}
                onPreview={(m, a) => onPreview(m, a)}
                refetch={() => {}}
                statuses={[]}
                tags={[]}
                isLast={index === messages.length - 1}
                participants={participants}
              ></ChatMessage>
            ) : (
              <div className={styles.historyRow} key={m.Id}>
                <div className={styles.line}></div>
                <div className={styles.text}>
                  {m.Content} (
                  {moment(m?.CreationDate)
                    .format("HH:mm DD.MM.YYYY")
                    .toString()}
                  )
                </div>
                <div className={styles.line}></div>
              </div>
            )
          )}
        </div>
      </div>
      <div className={styles.newMessageWrapper}>
        <ChatViewNewMessage
          chatId={chatId}
          chatPublicKey={chatData?.data.EncryptionKey.PublicKey}
          refetch={refetch}
          issueId=""
        ></ChatViewNewMessage>
      </div>

      {blob && (
        <div className={styles.PDFWrapper} ref={pdfRef}>
          <PDFViewer
            showTitle
            file={blob}
            title={previewFile?.FileName}
            onCloseButton={() => {
              pdfRef.current.classList.remove(styles.show);
              pdfRef.current.classList.add(styles.hide);
              setTimeout(() => {
                removeBlob();
              }, 1000);
            }}
            showHeader={true}
            setShowComments={() => setShowComments(!showComments)}
          />
        </div>
      )}

      {showParticipantsModal && (
        <div className={styles.modal}>
          <div className={styles.bg}></div>
          <div className={styles.modalCenter}>
            <ChatParticipantsModal
              sourceParticipants={participants}
              toggle={() => setShowParticipantsModal(false)}
              onApply={(v) => {
                updateParticipants(v);
              }}
            ></ChatParticipantsModal>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatView;
